<template>
  <div class="w-64">
    <div
      class="flex items-center justify-between mb-2 pb-2 border-b border-[var(--p-surface-200)] dark:border-[var(--p-primary-400)]/30"
    >
      <h3 class="font-medium text-sm text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]">
        {{
          showMenu
            ? t('whatsapp.file_upload.send_files')
            : t('whatsapp.file_upload.selected_count', { count: selectedFiles.length })
        }}
      </h3>
      <Button text rounded severity="secondary" @click="emit('close')">
        <template #icon>
          <X :size="18" />
        </template>
      </Button>
    </div>

    <div v-if="showMenu" class="space-y-0.5">
      <Button
        v-for="option in fileTypeOptions"
        :key="option.id"
        :label="option.label"
        text
        class="w-full !justify-start h-auto py-2 px-2 text-xs! !text-[var(--p-surface-700)] dark:!text-[var(--p-primary-50)]"
        @click="handleFileTypeSelect(option)"
      >
        <template #icon>
          <component :is="option.icon" :size="16" :stroke-width="2" class="mr-2" />
        </template>
      </Button>
    </div>

    <div v-else>
      <input
        ref="fileInputRef"
        type="file"
        multiple
        class="hidden"
        :accept="currentAccept"
        :capture="currentCapture"
        @change="handleFileChange"
      />

      <div v-if="selectedFiles.length === 0" class="text-center py-8">
        <p class="text-xs mb-4 text-[var(--p-surface-500)] dark:text-[var(--p-primary-50)] ">
          {{ t('whatsapp.file_upload.no_files_selected') }}
        </p>
        <AppButton
          :label="t('whatsapp.file_upload.back_to_menu')"
          outlined
          size="small"
          class="w-full dark:!text-[var(--p-primary-300)] dark:border-[var(--p-primary-300)]!"
          @click="handleBackToMenu"
        />
      </div>

      <div v-else>
        <div class="max-h-48 overflow-y-auto mb-4 space-y-2">
          <div
            v-for="(file, index) in selectedFiles"
            :key="index"
            class="flex items-center gap-3 p-2 rounded-lg bg-[var(--p-surface-100)] dark:bg-[var(--p-surface-800)] hover:bg-[var(--p-surface-200)] dark:hover:bg-[var(--p-surface-700)] transition-colors"
          >
            <component
              :is="getFileIcon(file)"
              :size="20"
              :stroke-width="2"
              class="text-[var(--p-surface-500)] flex-shrink-0"
            />
            <div class="flex-1 min-w-0">
              <p
                class="text-sm font-medium truncate text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]"
              >
                {{ file.name }}
              </p>
              <p class="text-xs text-[var(--p-surface-500)]">{{ formatFileSize(file.size) }}</p>
            </div>
            <Button text rounded severity="danger" @click="removeFile(index)">
              <template #icon>
                <X :size="18" />
              </template>
            </Button>
          </div>
        </div>

        <div class="flex gap-2">
          <Button
            :label="t('whatsapp.file_upload.back')"
            outlined
            size="small"
            class="flex-1"
            @click="handleBackToMenu"
          />
          <Button
            :label="t('whatsapp.file_upload.send')"
            severity="primary"
            size="small"
            class="flex-1"
            @click="handleSend"
          />
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue'
import Button from 'primevue/button'
import { FileText, Image, Camera, Music, FolderOpen, X } from 'lucide-vue-next'
import type { Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFileFormatter } from '../../composables/useFileFormatter'
import type { FilePreviewCategory } from '../../composables/useFiles'
import AppButton from '@/components/atoms/buttons/AppButton.vue'

const emit = defineEmits<{
  'files-select': [files: File[], category?: FilePreviewCategory]
  close: []
  'open-camera': []
}>()

const { formatFileSize } = useFileFormatter()
const { t } = useI18n()

const fileInputRef = ref<HTMLInputElement | null>(null)
const selectedFiles = ref<File[]>([])
const showMenu = ref(true)
const currentAccept = ref('*')
const currentCapture = ref<'environment' | undefined>(undefined)
const currentCategory = ref<FilePreviewCategory>('all')
const isFilePickerOpen = ref(false)

const fileTypeOptions = computed(() => [
  {
    id: 'document',
    label: t('whatsapp.file_upload.document'),
    icon: FileText,
    accept: '.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z',
    category: 'document' as const,
  },
  {
    id: 'photos-videos',
    label: t('whatsapp.file_upload.photos_and_videos'),
    icon: Image,
    accept: '.jpg,.jpeg,.png,image/jpeg,image/png,video/*,.mov',
    category: 'media' as const,
  },
  {
    id: 'camera',
    label: t('whatsapp.file_upload.camera'),
    icon: Camera,
    accept: '.jpg,.jpeg,.png,image/jpeg,image/png',
    capture: 'environment' as const,
    category: 'image' as const,
  },
  {
    id: 'audio',
    label: t('whatsapp.file_upload.audio'),
    icon: Music,
    // Meta-compatible formats only: AAC, AMR, MP3, MP4-audio (m4a), OGG/OPUS
    accept: '.aac,.amr,.mp3,.m4a,.ogg,audio/aac,audio/amr,audio/mpeg,audio/mp4,audio/ogg',
    category: 'audio' as const,
  },
  {
    id: 'all-files',
    label: t('whatsapp.file_upload.all_files'),
    icon: FolderOpen,
    accept: '*',
    category: 'all' as const,
  },
])

const handleFileTypeSelect = (option: (typeof fileTypeOptions.value)[number]) => {
  currentAccept.value = option.accept
  currentCapture.value = option.capture
  currentCategory.value = option.category
  showMenu.value = false

  if (option.id === 'camera') {
    showMenu.value = true
    emit('open-camera')
    emit('close')
    return
  }

  openFilePicker()
}

const handleFileChange = (e: Event) => {
  isFilePickerOpen.value = false
  const target = e.target as HTMLInputElement
  if (target.files) {
    const MAX_SIZE = 16 * 1024 * 1024 // 16 MB — Meta limit
    const filesArray = Array.from(target.files)
    const oversized = filesArray.filter((f) => f.size > MAX_SIZE)
    if (oversized.length > 0) {
      alert(
        t('whatsapp.file_upload.files_too_large', {
          files: oversized.map((f) => f.name).join('\n'),
        }),
      )
    }
    const valid = filesArray.filter((f) => f.size <= MAX_SIZE)
    if (valid.length > 0) {
      emit('files-select', valid, currentCategory.value)
      selectedFiles.value = []
      showMenu.value = true
      emit('close')
    } else {
      selectedFiles.value = []
      showMenu.value = true
      emit('close')
    }

    target.value = ''
  }
}

const openFilePicker = () => {
  isFilePickerOpen.value = true

  window.addEventListener(
    'focus',
    () => {
      setTimeout(() => {
        if (!isFilePickerOpen.value) return
        isFilePickerOpen.value = false
        selectedFiles.value = []
        showMenu.value = true
        emit('close')
      }, 250)
    },
    { once: true },
  )

  setTimeout(() => {
    fileInputRef.value?.click()
  }, 0)
}

const removeFile = (index: number) => {
  selectedFiles.value = selectedFiles.value.filter((_, i) => i !== index)
}

const handleSend = () => {
  if (selectedFiles.value.length > 0) {
    emit('files-select', selectedFiles.value, currentCategory.value)
    selectedFiles.value = []
    emit('close')
  }
}

const handleBackToMenu = () => {
  selectedFiles.value = []
  showMenu.value = true
}

onUnmounted(() => {
  isFilePickerOpen.value = false
})

const getFileIcon = (file: File): Component => {
  if (file.type.startsWith('image/')) return Image
  if (file.type.startsWith('video/')) return Image
  if (file.type.startsWith('audio/')) return Music
  if (file.type.includes('pdf') || file.type.includes('document')) return FileText
  return FolderOpen
}
</script>
